import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, ImagePlus, Loader2, Save, Phone, Plus } from "lucide-react";
import { CATEGORIES } from "@/lib/categories";
import { AIR_BENGUE } from "@/lib/airBengue";
import { getUser } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (u) => {
  if (!u.startsWith("/api")) return u;
  const user = getUser();
  return `${process.env.REACT_APP_BACKEND_URL}${u}${user ? `?viewer_id=${user.id}` : ""}`;
};

export const EditAnnonceModal = ({ annonce, offre, profileId, onClose, onSaved }) => {
  const cat = CATEGORIES[annonce.categorie] || AIR_BENGUE;
  const [texte, setTexte] = useState(annonce.texte || "");
  const [nom, setNom] = useState(annonce.nom_annonceur || "");
  // Air-Bengue : numéros internationaux conservés tels quels (« +32470112233 ») ;
  // les autres rubriques restent sur 10 chiffres ivoiriens.
  const international = annonce.categorie === "air";
  const [telephones, setTelephones] = useState(
    (annonce.telephones?.length ? annonce.telephones : [""]).map((t) =>
      international
        ? String(t).replace(/[^\d+]/g, "")
        : String(t).replace(/\D/g, "").slice(-10)),
  );
  const [photos, setPhotos] = useState(annonce.photos || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const photoInput = useRef(null);

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > offre.photosMax) {
      toast.error(`Maximum ${offre.photosMax} photo(s) pour ce palier.`);
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/annonces/upload`, fd);
        setPhotos((prev) => [...prev, res.data]);
      }
    } catch {
      toast.error("Échec de l'upload d'une photo.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!photos.length) {
      toast.error("Conservez au moins une photo.");
      return;
    }
    const numeros = telephones
      .map((t) => (international ? t.replace(/[^\d+]/g, "") : t.replace(/\D/g, "")))
      .filter(Boolean);
    const invalide = international
      ? numeros.some((n) => n.replace(/\D/g, "").length < 8 || n.replace(/\D/g, "").length > 15)
      : numeros.some((n) => n.length !== 10);
    if (invalide) {
      toast.error("Numéro invalide", {
        description: international
          ? "Indiquez le numéro avec son indicatif pays (ex. +32 470 11 22 33)."
          : "Chaque numéro doit comporter 10 chiffres.",
      });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/annonces/${annonce.id}`, {
        profile_id: profileId,
        texte,
        nom_annonceur: nom,
        telephones: numeros,
        air: annonce.air || null,
        photos,
        video: annonce.video || null,
      });
      toast.success(res.data.message);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="edit-annonce-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 28, scale: 0.97 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-7 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="edit-annonce-modal"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-stone-900">Modifier l'annonce</h2>
              <p className="text-xs font-semibold mt-1" style={{ color: cat.color }}>{cat.name}</p>
            </div>
            <button onClick={onClose} data-testid="edit-annonce-close" className="text-stone-400 hover:text-stone-700 transition-colors p-1">
              <X size={20} />
            </button>
          </div>

          <div className="mt-6">
            <label className="text-sm font-semibold text-stone-700">Nom affiché</label>
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value.slice(0, 40))}
              placeholder="Nom visible sur l'annonce"
              data-testid="edit-annonce-nom"
              className="mt-2 w-full rounded-2xl border border-[#E5DFD5] bg-[#FAF8F5] px-4 py-3 text-sm font-semibold text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300"
            />
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-stone-700">
              Numéros à contacter
              <span className="ml-2 text-[11px] font-normal text-stone-400">
                (changez-les en cas de perte de téléphone)
              </span>
            </label>
            <div className="mt-2 space-y-2">
              {telephones.map((num, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="flex flex-1 items-center gap-2 rounded-2xl border border-[#E5DFD5] bg-[#FAF8F5] px-4 py-2.5">
                    <Phone size={14} className="shrink-0 text-stone-400" />
                    {!international && (
                      <span className="text-sm font-semibold text-stone-500">+225</span>
                    )}
                    <input
                      value={num}
                      onChange={(e) => setTelephones(telephones.map((t, idx) =>
                        idx === i
                          ? (international
                              ? e.target.value.replace(/[^\d+]/g, "").slice(0, 16)
                              : e.target.value.replace(/\D/g, "").slice(0, 10))
                          : t))}
                      inputMode={international ? "tel" : "numeric"}
                      placeholder={international ? "+32 470 11 22 33" : "07 07 07 07 07"}
                      data-testid={`edit-annonce-telephone-${i}`}
                      className="w-full min-w-0 bg-transparent text-sm font-semibold tracking-wide text-stone-800 outline-none placeholder:font-normal placeholder:text-stone-300"
                    />
                  </span>
                  {telephones.length > 1 && (
                    <button
                      onClick={() => setTelephones(telephones.filter((_, idx) => idx !== i))}
                      data-testid={`edit-annonce-telephone-supprimer-${i}`}
                      aria-label="Retirer ce numéro"
                      className="rounded-full border border-[#E5DFD5] p-2.5 text-stone-400 transition-colors hover:border-red-300 hover:text-red-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {telephones.length < 4 && (
              <button
                onClick={() => setTelephones([...telephones, ""])}
                data-testid="edit-annonce-telephone-ajouter"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-4 py-2 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900"
              >
                <Plus size={13} /> Ajouter un numéro
              </button>
            )}
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-stone-700">Texte de l'annonce</label>
            <textarea
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              maxLength={1000}
              rows={6}
              placeholder="Décrivez votre annonce…"
              data-testid="edit-annonce-texte"
              className="mt-2 w-full bg-[#FAF8F5] border border-[#E5DFD5] rounded-2xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300 resize-none"
            />
            <div className="text-right text-[11px] text-stone-400" data-testid="edit-annonce-compteur">{texte.length}/1000 caractères</div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-stone-700">Photos ({photos.length}/{offre.photosMax})</label>
              <button
                onClick={() => photoInput.current?.click()}
                disabled={uploading || photos.length >= offre.photosMax}
                data-testid="edit-annonce-add-photo"
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border-2 disabled:opacity-40 transition-colors"
                style={{ borderColor: cat.color, color: cat.color }}
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Ajouter
              </button>
            </div>
            <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={onPhotos} data-testid="edit-annonce-photo-input" />
            <div className="flex flex-wrap gap-3">
              {photos.map((p, i) => (
                <div key={p.hash + i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#E5DFD5]">
                  <img src={mediaUrl(p.url)} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                    data-testid={`edit-annonce-remove-photo-${i}`}
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={save}
            disabled={saving || uploading}
            data-testid="edit-annonce-save"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-full transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: cat.color }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Enregistrer les modifications
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
